import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Loader2, Wand2, Copy, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { absoluteUrl, getPublicTool } from "@/lib/tools-catalog";
import { buildToolSoftwareApplicationJsonLd } from "@/lib/seo/tool-structured-data";

export const Route = createFileRoute("/tools/paraphraser")({
  head: () => {
    const tool = getPublicTool("paraphraser");
    const url = absoluteUrl(tool.path);
    const title = tool.pageTitle;
    const description = tool.description;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildToolSoftwareApplicationJsonLd(tool)),
        },
      ],
    };
  },
  component: ParaphraserPage,
});

type Mode = "formal" | "simple" | "shorten" | "expand";
type Captcha = { question: string; token: string };

function ParaphraserPage() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<Mode>("formal");
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState<Captcha | null>(null);
  const [answer, setAnswer] = useState("");
  const words = useMemo(
    () => (input.trim() ? input.trim().split(/\s+/).length : 0),
    [input],
  );

  async function loadCaptcha() {
    try {
      const r = await fetch("/api/public/tools/paraphrase");
      if (!r.ok) throw new Error();
      setCaptcha((await r.json()) as Captcha);
      setAnswer("");
    } catch {
      toast.error("Couldn't load captcha");
    }
  }
  useEffect(() => {
    void loadCaptcha();
  }, []);

  async function run() {
    if (words < 4) return toast.error("Paste at least a short paragraph");
    if (!captcha || !answer.trim()) return toast.error("Answer the captcha");
    setLoading(true);
    setOutput("");
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const res = await fetch("/api/public/tools/paraphrase", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          text: input,
          mode,
          captchaToken: captcha.token,
          captchaAnswer: Number(answer),
        }),
      });
      const data = await res.json();
      if (data?.nextCaptcha) {
        setCaptcha(data.nextCaptcha as Captcha);
        setAnswer("");
      } else if (data?.needsNewCaptcha) {
        void loadCaptcha();
      }
      if (!res.ok) {
        toast.error(data?.error || "Failed");
        return;
      }
      setOutput(data.text);
      if (data.billed) toast.success(`Done — 1 credit used. Balance: ${data.balance ?? "—"}`);
      else toast.success("Done");
    } catch (e: any) {
      toast.error(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container-narrow py-12">
      <p className="text-xs uppercase tracking-[0.25em] text-gold">Free tool</p>
      <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">AI Paraphraser</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Rewrite a passage in a formal, plain, shorter or expanded style — without losing citations,
        names or figures. Three free paraphrases per day; sign in to keep going at 1 credit each.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex flex-wrap gap-2 text-xs">
            {(["formal", "simple", "shorten", "expand"] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`rounded-full border px-3 py-1 capitalize ${
                  mode === m
                    ? "border-navy bg-navy text-white"
                    : "border-input bg-background text-muted-foreground"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={14}
            maxLength={4000}
            placeholder="Paste the passage you want to paraphrase…"
            className="w-full rounded-md border border-input bg-background p-3 text-sm"
          />
          <div className="mt-2 text-xs text-muted-foreground">{words} words</div>

          <div className="mt-3 rounded-md border border-input bg-muted/30 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-navy">
              <ShieldCheck className="h-4 w-4 text-gold" /> Human check
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-mono">{captcha?.question ?? "…"} =</span>
              <input
                type="number"
                inputMode="numeric"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={loadCaptcha}
                className="text-xs text-muted-foreground underline"
              >
                new
              </button>
            </div>
          </div>

          <button
            onClick={run}
            disabled={loading || words < 4 || !captcha || !answer.trim()}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Paraphrase
          </button>
        </div>

        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-muted-foreground">
            Output
          </label>
          <textarea
            value={output}
            readOnly
            rows={18}
            placeholder="The paraphrased passage will appear here."
            className="w-full rounded-md border border-input bg-muted/30 p-3 text-sm"
          />
          {output && (
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(output);
                  toast.success("Copied");
                }}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([output], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "paraphrased.txt";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-xs"
              >
                <Download className="h-3.5 w-3.5" /> Download .txt
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
